import { type ReactNode } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './card';
import { motion } from 'framer-motion';
import { ResponsiveContainer, AreaChart, Area } from 'recharts';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface KPICardProps {
  title: string;
  value: string;
  icon: ReactNode;
  subtitle?: string;
  /** Sparkline data points (last 7 values) */
  sparkline?: number[];
  /** Delta text like "+12%" or "-3%" */
  delta?: string;
  /** Whether the delta is positive (green), negative (red), or neutral */
  deltaType?: 'positive' | 'negative' | 'neutral';
}

const deltaConfig = {
  positive: {
    className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    icon: TrendingUp,
  },
  negative: {
    className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    icon: TrendingDown,
  },
  neutral: {
    className: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
    icon: Minus,
  },
} as const;

export function KPICard({
  title,
  value,
  icon,
  subtitle,
  sparkline,
  delta,
  deltaType = 'neutral',
}: KPICardProps) {
  const sparkData = sparkline?.map((v) => ({ v }));
  const { className: deltaCls, icon: DeltaIcon } = deltaConfig[deltaType];

  return (
    <Card className="hover:shadow-md transition-all duration-200 hover:-translate-y-0.5">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle>{title}</CardTitle>
        <span className="text-muted-foreground">{icon}</span>
      </CardHeader>
      <CardContent>
        <div className="flex items-end justify-between gap-4">
          <div className="min-w-0">
            <motion.div
              className="text-2xl font-bold"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              key={value}
            >
              {value}
            </motion.div>

            {delta && (
              <span
                className={`mt-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${deltaCls}`}
              >
                <DeltaIcon className="h-3 w-3" />
                {delta}
              </span>
            )}
          </div>

          {sparkData && sparkData.length > 1 && (
            <div className="h-10 w-24 shrink-0">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={sparkData}>
                  <defs>
                    <linearGradient id="sparkFill" x1="0" y1="0" x2="0" y2="1">
                      <stop
                        offset="0%"
                        stopColor="var(--color-primary)"
                        stopOpacity={0.1}
                      />
                      <stop
                        offset="100%"
                        stopColor="var(--color-primary)"
                        stopOpacity={0}
                      />
                    </linearGradient>
                  </defs>
                  <Area
                    type="monotone"
                    dataKey="v"
                    stroke="var(--color-primary)"
                    strokeWidth={1.5}
                    fill="url(#sparkFill)"
                    isAnimationActive={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {subtitle && (
          <p className="mt-1 text-xs text-muted-foreground">{subtitle}</p>
        )}
      </CardContent>
    </Card>
  );
}
